/**
 * Backend correlation via the W3C **baggage** header (parity story).
 *
 * The fetch/XHR auto-instrumentations already inject `traceparent` on requests to
 * allowed backends; this adds `session.id` to the **baggage** header on those same
 * requests, so the server spans (Strapi / API / KV) can read the session identity
 * and stamp it server-side — making `session.id ⇄ backend span` traversable
 * beyond the `trace_id` link.
 *
 * We wrap `window.fetch` (installed AFTER OTel's fetch instrumentation, so our
 * wrapper is the outermost) and append `session.id` to any existing `baggage`
 * header, but ONLY for URLs matching `backendUrls` — never leaking the id to
 * third parties. Best-effort and reversible (returns an uninstall fn).
 *
 * Gated by the `propagateBaggage` init option; the allowlist defaults to
 * `propagateTraceHeaderCorsUrls` (the same backends that receive `traceparent`).
 */
type Matcher = string | RegExp

function matches(url: string, matchers: Matcher[]): boolean {
  return matchers.some((m) => (typeof m === 'string' ? url.startsWith(m) : m.test(url)))
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

/** Merge `session.id=<id>` into an existing W3C `baggage` header value. */
export function withSessionBaggage(existing: string | null, sessionId: string): string {
  const entry = `session.id=${encodeURIComponent(sessionId)}`
  if (!existing) return entry
  // Don't duplicate if a session.id is already present (e.g. nested wrappers).
  const hasSession = existing.split(',').some((p) => p.trim().startsWith('session.id='))
  return hasSession ? existing : `${existing},${entry}`
}

/**
 * Install the baggage propagation fetch wrapper. No-op (returns a no-op
 * uninstaller) when there is no `window.fetch`. `getId` is the session-id source
 * ({@link import('./session.js').getSessionId}); `ignoreUrls` excludes the
 * telemetry pipeline itself.
 */
export function installBaggagePropagation(
  getId: () => string,
  backendUrls: Matcher[],
  ignoreUrls: Matcher[] = [],
): () => void {
  if (
    typeof window === 'undefined' ||
    typeof window.fetch !== 'function' ||
    backendUrls.length === 0
  ) {
    return () => {}
  }

  const originalFetch = window.fetch.bind(window)

  window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
    let sessionId = ''
    let url = ''
    try {
      sessionId = getId()
      url = resolveUrl(input)
    } catch {
      return originalFetch(input, init)
    }

    // Skip non-backend URLs and the telemetry pipeline itself.
    if (!sessionId || !matches(url, backendUrls) || matches(url, ignoreUrls)) {
      return originalFetch(input, init)
    }

    try {
      const headers = new Headers(
        init?.headers ?? (input instanceof Request ? input.headers : undefined),
      )
      headers.set('baggage', withSessionBaggage(headers.get('baggage'), sessionId))
      return originalFetch(input, { ...init, headers })
    } catch {
      // Header juggling failed → fall back to an unmodified request.
      return originalFetch(input, init)
    }
  }

  return () => {
    window.fetch = originalFetch
  }
}
